import useSWR from 'swr';
import chunk from 'lodash/chunk';
import { sdk } from '@/sdk';
import { Product } from '@shared/types/product';

const MAX_SKUS_PER_QUERY = 50;

type FetchProductArgs = {
  searchQuery?: string;
  skus?: string[];
  limit?: number;
  storeKey?: string;
  customerCategoryChannelKey?: string;
  distributionChannelId?: string;
  isInitialSearch?: boolean;
  getAllProducts?: boolean;
  productTypeKey?: string;
  isCommercialOperation?: boolean;
  category?: string;
};

const fetchProducts = async ({
  searchQuery,
  skus,
  limit,
  storeKey,
  customerCategoryChannelKey,
  distributionChannelId,
  isInitialSearch,
  getAllProducts,
  productTypeKey,
  isCommercialOperation,
  category,
}: FetchProductArgs): Promise<{ items: Product[]; isError: boolean }> => {
  if (skus && skus.length > MAX_SKUS_PER_QUERY) {
    const skuChunks = chunk(skus, MAX_SKUS_PER_QUERY);

    const results = await Promise.all(
      skuChunks.map((chunkedSkus) => {
        return sdk.composableCommerce.product.query({
          query: searchQuery,
          skus: chunkedSkus,
          limit: chunkedSkus.length,
          storeKey,
          customerCategoryChannelKey,
          distributionChannelId,
          isInitialSearch,
          getAllProducts,
          productTypeKey,
          isCommercialOperation,
          category,
        });
      }),
    );

    const allItems = results.flatMap((res) => (res.isError ? [] : (res.data?.items as Product[])));
    const hasError = results.some((res) => res.isError);

    return {
      items: allItems,
      isError: hasError,
    };
  }

  const res = await sdk.composableCommerce.product.query({
    query: searchQuery,
    skus,
    limit,
    storeKey,
    customerCategoryChannelKey,
    distributionChannelId,
    isInitialSearch,
    getAllProducts,
    productTypeKey,
    isCommercialOperation,
    category,
  });

  return {
    items: res.isError ? [] : (res.data?.items as Product[]),
    isError: res.isError,
  };
};

const useProductSearch = (
  searchQuery?: string,
  skus?: string[],
  limit?: number,
  storeKey?: string,
  customerCategoryChannelKey?: string,
  distributionChannelId?: string,
  isInitialSearch?: boolean,
  getAllProducts?: boolean,
  productTypeKey?: string,
  isCommercialOperation?: boolean,
) => {
  const key = [
    '/action/product/query',
    storeKey,
    searchQuery,
    skus,
    limit,
    isInitialSearch,
    getAllProducts,
    productTypeKey,
    isCommercialOperation,
  ];

  const fetcher = () =>
    fetchProducts({
      searchQuery,
      skus,
      limit,
      storeKey,
      customerCategoryChannelKey,
      distributionChannelId,
      isInitialSearch,
      getAllProducts,
      productTypeKey,
      isCommercialOperation,
    });

  const { data, error, mutate, isValidating } = useSWR(key, fetcher, {
    revalidateOnMount: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const products = error || data?.isError ? [] : data?.items ?? [];

  const loadManually = async (params: FetchProductArgs): Promise<Product[]> => {
    const res = await fetchProducts(params);
    return res.items;
  };

  return {
    products,
    load: mutate,
    loadManually,
    isLoading: isValidating,
    isError: !!error,
  };
};

export default useProductSearch;
